// tslint:disable: max-line-length
import {
    Compiler,
    ComponentFactory,
    Injectable,
    ModuleWithComponentFactories,
    NgModuleFactory,
    SystemJsNgModuleLoader
} from "@angular/core";
import { IService, mapIServiceToTenantAppType, TCITenantAppType, TCITenantSelectorType } from "@tci/common";
import { TCITenantRemoteServices } from "./tci-tenant-remote-services";
import { TciTenantService } from "./tci-tenant.service";
import { Observable } from "rxjs";
import { TciTenantModelService } from "./tci-tenant.model";
import { AbstractTCITenantComponent, DistinctionKeyFunction } from "./tci-tenant-component";

export interface ITCITenantCacheOptions {
    routeId: string;
    subRouteId?: string;
    tenantType?: TCITenantAppType;
}

export interface ITCIResFactory<T, C> {
    storeKey: string;
    tenantType: TCITenantAppType;
    modFactory: NgModuleFactory<T>;
    cmpFactory: ComponentFactory<C>;
    ngModFactory: NgModuleFactory<any>;
    distinctKeyFn: DistinctionKeyFunction<any>;
}

export interface ITCITenantCompCacheService {
    onInit(): Observable<boolean>;

    /**
     * get Component Cache
     * @param refresh
     * @param options
     */
    getCache(refreshboolean, options: ITCITenantCacheOptions);

    /**
     * @deprecated
     * @param tenantType
     * @param selectorType
     * @param refresh
     */
    getComponent(
        tenantType: TCITenantAppType,
        selectorType: TCITenantSelectorType,
        refresh?: boolean): Observable<ITCIResFactory<any, any>>;
}

export interface ITCIComponentCacheLoader {
    /**
     * The component should be already compiled for this to work
     */
    getComponentFromCache(tenantType: TCITenantAppType, selectorType: TCITenantSelectorType): ITCIResFactory<any, any>;
}

export interface ITCITenantCompCache {
    [tenant_selector: string]: ITCIResFactory<any, any>;
}

interface ITenantModFactory {
    tenantType: TCITenantAppType;
    modFactory: NgModuleFactory<any>;
    selectorType: TCITenantSelectorType;
}

interface ITenantCompileResult {
    storeKey: string;
    resFactory: ITCIResFactory<any, any>;
}

const SEPARATOR = ":";

@Injectable()
export class TciTenantComponentCacheService implements ITCITenantCompCacheService, ITCIComponentCacheLoader {
    constructor(private loader: SystemJsNgModuleLoader,
        private compiler: Compiler,
        private tenantRemoteServices: TCITenantRemoteServices,
        private tenantSvc: TciTenantService,
        private tenantModelService: TciTenantModelService) {

    }

    private static instance: ITCITenantCompCacheService;
    private static partialRefresh = false;
    private static fullRefresh = false;

    private static cachePollingWorker: { [key: string]: Observable<ITCITenantCompCache> } = {}; // Stores the cache pooling job Observable
    private compCache = {};

    /**
     *
     * @param loader
     * @param compiler
     * @param tenantRemoteServices
     * @param tenantSvc
     * @param tenantModelSvc
     */
    public static getInstance(loader: SystemJsNgModuleLoader,
        compiler: Compiler,
        tenantRemoteServices: TCITenantRemoteServices,
        tenantSvc: TciTenantService,
        tenantModelSvc: TciTenantModelService): ITCITenantCompCacheService {
        if (!TciTenantComponentCacheService.instance) {
            TciTenantComponentCacheService.instance = new TciTenantComponentCacheService(
                loader, compiler, tenantRemoteServices, tenantSvc, tenantModelSvc
            );
        }
        return TciTenantComponentCacheService.instance;
    }

    public onInit(): Observable<boolean> {
        return this.tenantSvc.onInit();
    }

    private getModKey(options: ITCITenantCacheOptions, type?: TCITenantAppType): string {
        if (!type) {
            type = options.tenantType;
        }

        try {
            return type.getComponentModuleName(options.routeId, options.subRouteId);
        } catch (e) {
            throw Error(e);
        }
    }

    // tslint:disable: member-ordering
    private svcModFacObj: { [key: string]: Observable<ITenantModFactory> } = {};
    private modFactsReady: { [key: string]: boolean } = {};
    private loadedModFacts: ITenantModFactory[] = [];

    private getComponentModuleFactories(options: ITCITenantCacheOptions): Observable<ITenantModFactory> {
        const modKey = this.getModKey(options);

        if (this.modFactsReady[modKey]) {
            return Observable.merge(...this.loadedModFacts.map(fac => Observable.of(fac)));
        }

        if (!this.svcModFacObj[modKey]) {
            let moduleName: string;
            this.svcModFacObj[modKey] = this.tenantRemoteServices.getRemoteServices()
                .flatMap(svc => svc)
                .filter(svc => mapIServiceToTenantAppType(svc).equals(options.tenantType))
                .filter(() => {
                    const cfg = (<any>SystemJS).getConfig();
                    return cfg.map && cfg.packages && cfg.map[modKey];
                })
                .flatMap((tenant: IService) => {
                    /**
                     * The tenant names are from tci-deployer i.e. "wistudio" and "scribe"
                     *  and we are getting the tenant names from backend API /api/v1/uiservices as they match the deployer names
                     *  We are banking on the moduleconfig.json map + package entry
                     *  where the tenant name suffixed with a "-svc" is mapped to the module e.g.
                     *  map:
                     *      "wistudio-svc" :  : "wi-host:",
                     *  package:
                     *      "wistudio-svc" : { "main": "app/app.child.module.js", "defaultExtension": "js" },
                     *
                     * NOTE that this entry is different from the app entry point e..g.
                     * map:
                     *      "wi-studio/client" : "wi-host:",
                     *      "wi-studio/client": "wi-host:",
                     *      "wi-studio/index": "wi-host:",
                     *  package:
                     *      "wi-studio/client": { "main": "app/app.child.module.js", "defaultExtension": "js" },
                     *      "wi-studio/index": { "main": "index.js", "defaultExtension": "js" },
                     *      "wi-studio": { "main": "index.js", "defaultExtension": "js" },
                     *
                     */
                    moduleName = TCITenantAppType.fromString(tenant.name).getComponentModuleName(options.routeId, options.subRouteId);
                    // console.log("Loading Module", moduleName);
                    return Observable.fromPromise(this.loader.load(moduleName))
                        .catch(err => {
                            console.error("The module was not able to be loaded", tenant, options.routeId, options.subRouteId, err);
                            return Observable.of(null);
                        })
                        .map(res => {
                            this.modFactsReady[modKey] = true;
                            return <ITenantModFactory>{
                                tenantType: TCITenantAppType.fromString(tenant.name),
                                modFactory: res
                            };
                        });
                })
                .map(modFac => {
                    this.loadedModFacts.push(modFac);
                    return modFac;
                })
                .finally(() => {
                    delete this.svcModFacObj[modKey];
                    if (!this.modFactsReady[modKey]) {
                        // console.error("The module was not loaded:", moduleName);
                    }
                })
                .share();
        }
        return this.svcModFacObj[modKey];
    }

    private moduleTypeObs: { [key: string]: Observable<ModuleWithComponentFactories<any>> } = {};
    private moduleTypeCache: { [key: string]: ModuleWithComponentFactories<any> } = {};

    private compile(svc: ITenantModFactory, options: ITCITenantCacheOptions): Observable<ITenantCompileResult> {
        let bundleKey = `${options.tenantType.toString()}-${options.routeId}`;
        if (options.subRouteId) {
            bundleKey += "-" + options.subRouteId;
        }

        let moduleObs: Observable<ModuleWithComponentFactories<any>>;
        if (this.moduleTypeCache[bundleKey]) {
            moduleObs = Observable.of(this.moduleTypeCache[bundleKey]);
            // console.log(">>> Reusing module fac");
        } else {
            if (!this.moduleTypeObs[bundleKey]) {
                // console.log(">>> Compiling Modules", bundleKey);
                this.moduleTypeObs[bundleKey] = Observable.fromPromise(this.compiler.compileModuleAndAllComponentsAsync<any>(svc.modFactory.moduleType))
                    .catch(e => {
                        console.error("The component was not found", svc);
                        return Observable.of({ componentFactories: [] } as any);
                    })
                    .map(moduleFac => {
                        moduleFac.componentFactories = moduleFac.componentFactories
                            .filter(data => {
                                if (data.componentType.prototype instanceof AbstractTCITenantComponent) {
                                    const annotations = data.componentType.prototype.getAnnotations();
                                    return annotations && annotations.tenantAppType.equals(svc.tenantType);
                                }
                                return false;
                            });

                        this.moduleTypeCache[bundleKey] = moduleFac;
                        return moduleFac;
                    })
                    .share();
            }

            moduleObs = this.moduleTypeObs[bundleKey];
        }

        return moduleObs
            // .map(mwcFactories => mwcFactories)
            .switchMap(mwcFactories => mwcFactories.componentFactories, (mwc, cmp) => ({
                cmpFactory: cmp,
                ngModFactory: mwc.ngModuleFactory
            }))
            .filter(data => svc.selectorType.isEqualTo(data.cmpFactory.selector))
            .map(data => {
                const distinctFn = data.cmpFactory.componentType.prototype.getAnnotations().distinctionKeyFn;
                const cacheStoreKey = `${svc.tenantType}${SEPARATOR}${svc.selectorType.toString()}`;
                return <ITenantCompileResult>{
                    storeKey: cacheStoreKey,
                    resFactory: {
                        storeKey: cacheStoreKey,
                        tenantType: svc.tenantType,
                        modFactory: svc.modFactory,
                        cmpFactory: data.cmpFactory,
                        ngModFactory: data.ngModFactory,
                        distinctKeyFn: distinctFn
                    }
                };
            });
    }

    /**
     * get Component Cache
     * @param refresh
     * @param options
     */
    getCache(refresh = false, options: ITCITenantCacheOptions): Observable<ITCITenantCompCache> {
        const modKey = this.getModKey(options);

        if (TciTenantComponentCacheService.cachePollingWorker[modKey] && !refresh) {
            return TciTenantComponentCacheService.cachePollingWorker[modKey]; // Share the cache working subject
        }

        let selectors: TCITenantSelectorType[] = [];
        return this.tenantModelService.onInit()
            .map(res => res && this.tenantModelService.getTenants())
            .flatMap(tenantMap => {
                if (options) {
                    if (options.tenantType) {
                        // selectors are not specified so use the the tenant preload map
                        const tenantType = options.tenantType;
                        const tenantObj = tenantMap[tenantType.toString()];
                        if (tenantObj) {
                            const preloadMap = tenantObj.getTenantComponentsPreloadMap();
                            selectors.push(...Object.keys(preloadMap).map((sel, idx) => TCITenantSelectorType.createPseudoSelector(sel)));
                        } else {
                            // console.warn("The module is not available:", JSON.stringify(options));
                        }
                    } else {
                        selectors = TCITenantSelectorType.getAllSelectors();
                    }
                }

                // const _selectors = options && options.selectors ? options.selectors : TCITenantSelectorType.getAllSelectors();
                const selectorsAvailable = options && !!options.tenantType;

                //  partial cache exists and fullrefresh has not happened and no selectors are present,
                //  it means this is called from places other than app list
                const fullRefreshNeeded = !selectorsAvailable && TciTenantComponentCacheService.partialRefresh && !TciTenantComponentCacheService.fullRefresh;

                if (!this.modFactsReady[modKey] || refresh || fullRefreshNeeded) {
                    // dont reset the cache as selected items have been previously loaded
                    if (fullRefreshNeeded) {
                        console.log("Cache Entries found:", Object.keys(this.compCache));
                    } else if (refresh) {
                        console.log("Initializing new cache");
                    }

                    TciTenantComponentCacheService.cachePollingWorker[modKey] = this.tenantSvc.onInit()
                        .flatMap(() => this.getComponentModuleFactories(options))
                        .flatMap(svc => {
                            const compilations = selectors.map(selectorType => {
                                const svcData: ITenantModFactory = {
                                    selectorType,
                                    tenantType: svc.tenantType,
                                    modFactory: svc.modFactory
                                };
                                return this.compile(svcData, options);
                            });
                            return Observable.merge(...compilations);
                        })
                        .toArray()
                        .map(res => {
                            const keyCounter: { [key: string]: number } = {};

                            res.forEach((val) => {
                                const selector = val.storeKey;
                                if (!Object.keys(keyCounter)) {
                                    keyCounter[selector] = 1;
                                } else {
                                    keyCounter[selector] = keyCounter[selector] + 1;
                                }
                            });

                            for (const key in Object.keys(keyCounter)) {
                                if (keyCounter[key] > 1) {
                                    throw new Error("Duplicate keys found :" + key);
                                }
                            }

                            res.forEach(val => this.compCache[val.storeKey] = val.resFactory); // Add the items to the cache collection

                            // console.log("Component Factory Cache for", modKey, ">", res);

                            if (selectorsAvailable) {
                                // Note if the app list init did a partial cache by passing selectors
                                if (!TciTenantComponentCacheService.partialRefresh) {
                                    TciTenantComponentCacheService.partialRefresh = true;
                                }
                            } else {
                                if (!TciTenantComponentCacheService.fullRefresh) {
                                    TciTenantComponentCacheService.fullRefresh = true;
                                }
                            }

                            return this.compCache;
                        })
                        .do(() => delete TciTenantComponentCacheService.cachePollingWorker[modKey])
                        .share();
                    return TciTenantComponentCacheService.cachePollingWorker[modKey];
                } // end else
                return Observable.of(this.compCache);
            });
    }

    // The component should be already compiled for this to work
    getComponentFromCache(tenantType: TCITenantAppType, selectorType: TCITenantSelectorType): ITCIResFactory<any, any> {
        const compKey = `${tenantType.toString()}${SEPARATOR}${selectorType.toString()}`;
        const comp: ITCIResFactory<any, any> = this.compCache[compKey];
        if (!comp) {
            // console.error("Requested component is not present", compKey);
        }

        return comp;
    }

    /**
     *
     * @param tenantType
     * @param selectorType
     * @param refresh
     * @deprecated
     */
    getComponent(
        tenantType: TCITenantAppType,
        selectorType: TCITenantSelectorType,
        refresh: boolean = false): Observable<ITCIResFactory<any, any>> {
        const cacheGetKey = `${tenantType.toString()}${SEPARATOR}${selectorType.toString()}`;
        const cacheOptions: ITCITenantCacheOptions = { routeId: undefined };
        return this.getCache(refresh, cacheOptions)
            .map(cache => {
                if (cache[cacheGetKey]) {
                    return cache[cacheGetKey];
                } else {
                    return null;
                }
            });
    }
}
